import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import organizationService from '../services/organizationService';
import { getSubdomain } from '../utils/subdomain';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Helper function to detect if user is logging in via Rocket platform
const isRocketPlatformUser = () => {
  // Check for Rocket platform indicators
  const hostname = window?.location?.hostname;
  const isRocketDomain = hostname?.includes('builtwithrocket') || 
                         hostname?.includes('rocket.net') ||
                         hostname?.endsWith('.rocket.new');
  
  // Check for Rocket platform metadata in session storage
  const rocketPlatformFlag = sessionStorage?.getItem('rocket_platform_user');
  
  return isRocketDomain || rocketPlatformFlag === 'true';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);

  useEffect(() => {
    // Check active session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session?.user?.id);
        loadUserRoles(session?.user?.id);
        loadUserOrganizations(session?.user?.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session?.user?.id);
        loadUserRoles(session?.user?.id);
        loadUserOrganizations(session?.user?.id);
      } else {
        setProfile(null);
        setUserRoles([]);
        setUserOrganizations([]);
        setCurrentOrganization(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          *,
          tenant:tenants(*),
          permissions(*)
        `)?.eq('id', userId)?.single();

      if (error) throw error;
      setProfile(data);
      
      // Set current organization from profile
      if (data?.tenant) {
        setCurrentOrganization({
          tenantId: data?.tenant?.id,
          tenantName: data?.tenant?.name,
          tenantSubdomain: data?.tenant?.subdomain,
          tenantLogoUrl: data?.tenant?.logo_url
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserOrganizations = async (userId) => {
    try {
      const organizations = await organizationService?.getUserOrganizations();
      setUserOrganizations(organizations);
      
      // Set current organization if not already set
      if (!currentOrganization && organizations?.length > 0) {
        const primary = organizations?.find(org => org?.isPrimary);
        const current = primary || organizations?.[0];
        setCurrentOrganization(current);
      }
    } catch (error) {
      console.error('Error loading user organizations:', error);
      setUserOrganizations([]);
    }
  };

  const loadUserRoles = async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_role_assignments')
        ?.select(`
          *,
          role:system_roles(
            id,
            name,
            display_name,
            hierarchy_level,
            role_permissions(
              feature,
              access_level
            )
          )
        `)
        ?.eq('user_id', userId);

      if (error) throw error;
      
      // Transform to include permissions in role structure
      const rolesWithPermissions = data?.map(assignment => ({
        ...assignment,
        role: {
          ...assignment?.role,
          permissions: assignment?.role?.role_permissions?.map(p => ({
            feature: p?.feature,
            accessLevel: p?.access_level
          })) || []
        }
      })) || [];
      
      setUserRoles(rolesWithPermissions);
    } catch (error) {
      console.error('Error loading user roles:', error);
      setUserRoles([]); // Safe default on error
    }
  };

  // Helper function to check if user is super admin
  const isSuperAdmin = () => {
    return userRoles?.some(ra => ra?.role?.hierarchy_level >= 100);
  };

  // Helper function to check if user is business admin
  const isBusinessAdmin = (tenantId = null) => {
    return userRoles?.some(ra => {
      const isAdmin = ra?.role?.hierarchy_level >= 80;
      if (!tenantId) return isAdmin;
      return isAdmin && ra?.tenant_id === tenantId;
    });
  };

  // Switch organization function
  const switchOrganization = async (tenantId) => {
    try {
      const success = await organizationService?.switchOrganization(tenantId);
      
      if (success) {
        // Find the organization from the list
        const newOrg = userOrganizations?.find(org => org?.tenantId === tenantId);
        if (newOrg) {
          setCurrentOrganization(newOrg);
        }
        
        // Reload profile to get updated tenant information
        if (user?.id) {
          await loadUserProfile(user?.id);
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    // Get current subdomain
    const subdomain = getSubdomain();
    
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    // Update last login
    await supabase?.from('user_profiles')?.update({ last_login_at: new Date()?.toISOString() })?.eq('id', data?.user?.id);

    // Check if this is a Rocket platform user and assign Super Admin role
    if (isRocketPlatformUser()) {
      await ensureSuperAdminRole(data?.user?.id);
    }
    
    // If on tenant subdomain, verify user belongs to this tenant
    if (subdomain) {
      await verifyTenantAccess(data?.user?.id, subdomain);
    }

    return data;
  };
  
  // Verify user has access to the tenant subdomain
  const verifyTenantAccess = async (userId, subdomain) => {
    try {
      // Get tenant by subdomain
      const { data: tenant, error: tenantError } = await supabase
        ?.from('tenants')
        ?.select('id')
        ?.eq('subdomain', subdomain)
        ?.single();
      
      if (tenantError || !tenant) {
        throw new Error(`Tenant not found for subdomain: ${subdomain}`);
      }
      
      // Check if user belongs to this tenant
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('tenant_id')
        ?.eq('id', userId)
        ?.single();
      
      if (profileError || !profile) {
        throw new Error('User profile not found');
      }
      
      // Super admins can access any tenant
      const { data: roles } = await supabase
        ?.from('user_role_assignments')
        ?.select('role:system_roles(hierarchy_level)')
        ?.eq('user_id', userId);
      
      const isSuperAdmin = roles?.some(r => r?.role?.hierarchy_level >= 100);
      
      if (!isSuperAdmin && profile?.tenant_id !== tenant?.id) {
        throw new Error('Access denied: User does not belong to this organization');
      }
    } catch (error) {
      console.error('Tenant access verification failed:', error);
      throw error;
    }
  };

  // Ensure user has Super Admin role (for Rocket platform users)
  const ensureSuperAdminRole = async (userId) => {
    try {
      // Get Super Admin role
      const { data: superAdminRole, error: roleError } = await supabase
        ?.from('system_roles')
        ?.select('id')
        ?.eq('name', 'super_admin')
        ?.single();

      if (roleError || !superAdminRole) {
        console.error('Super Admin role not found:', roleError);
        return;
      }

      // Check if user already has Super Admin role
      const { data: existingAssignment } = await supabase
        ?.from('user_role_assignments')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('role_id', superAdminRole?.id)
        ?.maybeSingle();

      // If not assigned, assign Super Admin role
      if (!existingAssignment) {
        const { error: assignError } = await supabase
          ?.from('user_role_assignments')
          ?.insert({
            user_id: userId,
            role_id: superAdminRole?.id,
            assigned_by: userId, // Self-assigned for Rocket platform users
            tenant_id: null // Super Admin has no tenant restriction
          });

        if (assignError) {
          console.error('Error assigning Super Admin role:', assignError);
        } else {
          console.log('Super Admin role assigned to Rocket platform user');
          // Reload user roles to reflect the change
          await loadUserRoles(userId);
        }
      }
    } catch (error) {
      console.error('Error ensuring Super Admin role:', error);
    }
  };

  const signUp = async (email, password, userData) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          tenant_id: userData?.tenantId,
          role: userData?.role || 'staff',
        },
      },
    });

    if (error) throw error;

    // Update additional profile fields
    if (data?.user && userData?.firstName) {
      await supabase?.from('user_profiles')?.update({
          first_name: userData?.firstName,
          last_name: userData?.lastName,
          phone: userData?.phone,
          job_title: userData?.jobTitle,
        })?.eq('id', data?.user?.id);
    }

    // Check if this is a Rocket platform user and assign Super Admin role
    if (data?.user && isRocketPlatformUser()) {
      await ensureSuperAdminRole(data?.user?.id);
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
    setUser(null);
    setProfile(null);
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');

    const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

    if (error) throw error;
    setProfile(data);
    return data;
  };

  const value = {
    user,
    profile,
    loading,
    userRoles,
    userOrganizations,
    currentOrganization,
    signIn,
    signUp,
    signOut,
    updateProfile,
    switchOrganization,
    isSuperAdmin,
    isBusinessAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};